import { ArrowDropDown } from '@mui/icons-material';
import { Box, Button, ClickAwayListener, Popper } from '@mui/material';
import React, { forwardRef, ReactNode, useRef, useState } from 'react';
import ccfColorPickerStyles from './ccf-color-picker.styles';
import colorData, { CustomStyleConfig, TEXT_COLOR_GROUP } from './color-data';

/**
 * Interface for CcfColorPickerProps
 */
export interface CcfColorPickerProps {
  /**
   * @remarks Used to show label for reset button
   */
  resetLabel?: string;
  /**
   * @remarks Used to get colors of different groups
   */
  group?: string;
  /**
   * @remarks  callback method used to return selected color to parent component
   */
  onSelect: (id: string | null, group: string) => void;
  /**
   * @remarks Used to pass icon from parent
   */
  children: ReactNode;
}

/**
 * Used to show color picker for Rich Text Editor
 * @param props -?- CcfColorPickerProps
 * @example - `<CcfColorPicker {...props} />`
 */
export const CcfColorPicker = forwardRef<
  HTMLButtonElement,
  CcfColorPickerProps
>(({ resetLabel = 'Reset', group = 'TEXT_COLOR', onSelect, children }, ref) => {
  const styles = ccfColorPickerStyles();
  const wrapperRef = useRef(null);
  const [openPicker, setColorPicker] = useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const colors: CustomStyleConfig = colorData.filter(
    (color) => color.group === group
  );

  /**
   * Method to toggle colorPicker
   *  @param event - MouseEvent or KeyboardEvent
   *  @example - toggleColorPicker(e)
   */
  const toggleColorPicker = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLSpanElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    setColorPicker((previousOpen) => !previousOpen);
  }

  /**
   * Method to handle color picker if clicked outside
   * @example `onOutsideClick()`
   */
  const onOutsideClick = () => {
    setAnchorEl(null);
    setColorPicker(false);
  };

  /**
   * Method to handle color change event
   * @param color - color
   * @example `handleColorSelect()`
   */
  const handleColorSelect = (
    id: string,
    group: string,
    event: React.MouseEvent<HTMLElement>,
    value: string
  ) => {
    onSelect(id, group);
    toggleColorPicker(event);
    fillIconColor(group, value);
  };

  /**
   * Method to handle reset color
   * @param event - MouseEvent
   * @example `handleRemoveColor(e)`
   */
  const handleRemoveColor = (event: React.MouseEvent<HTMLElement>) => {
    onSelect(null, group);
    toggleColorPicker(event);
    fillIconColor(group, '#333333')
  };

  /**
   * Method of fill color to text Icon / background Icon
   * @param group -color group
   * @example `fillIconColor(group,value)`
   */
  const fillIconColor = (group: string, value: string) => {
    group === TEXT_COLOR_GROUP ? document.querySelector('[data-testid="FormatColorTextIcon"]')?.querySelectorAll('path')[0]?.setAttribute('fill', value) : document.querySelector('[data-testid="BorderColorIcon"]')?.querySelectorAll('path')[0]?.setAttribute('fill', value);
  };

  return (
    <div ref={wrapperRef} style={styles.ColorPickerContainer}>
      <span role="presentation" ref={ref}
        onClick={toggleColorPicker} style={{ cursor: 'pointer' }}>
        {children}
        <ArrowDropDown />
      </span>
      <Popper
        open={openPicker}
        anchorEl={anchorEl}
        placement="top"
        style={{ zIndex: 1000 }}
      >
        <ClickAwayListener onClickAway={onOutsideClick}>
          <Box sx={styles.ColorPickerPopover}>
            <Button sx={styles.ColorResetButton} onClick={handleRemoveColor}>
              {resetLabel}
            </Button>
            <Box sx={styles.ColorButtonContainer} >
              {colors.map(({ id, label, value, group }) => (
                <Box component='button'
                  title={label}
                  sx={{ ...styles.ColorButton, backgroundColor: value }}
                  onClick={(event: React.MouseEvent<HTMLElement>) => handleColorSelect(id, group, event, value)}
                  data-testid={id}
                >
                </Box>
              ))}
            </Box>
          </Box>
        </ClickAwayListener>
      </Popper>
    </div>
  );
});

export default CcfColorPicker;
